import { Controller } from "@hotwired/stimulus"

const SVG_NS = "http://www.w3.org/2000/svg"

export default class extends Controller {
  static targets = ["canvas"]
  static values = { data: Array }

  connect() {
    this.lastWidth = 0

    // The chart is drawn at the canvas's measured pixel width. On initial load the
    // grid may not be laid out yet (width 0), and a sibling widget's resize reflows
    // this one without re-running connect(). A ResizeObserver redraws whenever the
    // container gains a real width or changes size, fixing both blank-on-load and the
    // resize ping-pong. (See planning_panel_controller for the same idiom.)
    if (this.hasCanvasTarget && window.ResizeObserver) {
      this.boundSchedule = this.scheduleRender.bind(this)
      this.observer = new ResizeObserver(this.boundSchedule)
      this.observer.observe(this.canvasTarget)
    } else {
      this.renderChart()
    }
  }

  disconnect() {
    this.observer?.disconnect()
    if (this.renderScheduled) {
      cancelAnimationFrame(this.renderFrame)
      this.renderScheduled = false
    }
  }

  scheduleRender() {
    if (this.renderScheduled) return
    this.renderScheduled = true
    this.renderFrame = requestAnimationFrame(() => {
      this.renderScheduled = false
      this.renderChart()
    })
  }

  renderChart() {
    const data = this.dataValue
    if (!this.hasCanvasTarget || data.length === 0) return

    const width = this.canvasTarget.clientWidth
    // Skip while the container has no layout width (would paint an invisible chart),
    // and skip redundant redraws when the width hasn't changed.
    if (width <= 0 || width === this.lastWidth) return
    this.lastWidth = width

    const height = 192
    const padding = { top: 20, right: 20, bottom: 30, left: 40 }
    const chartWidth = width - padding.left - padding.right
    const chartHeight = height - padding.top - padding.bottom

    const maxValue = Math.max(...data.map((d) => d.value), 1)
    const barWidth = (chartWidth / data.length) * 0.7
    const barGap = (chartWidth / data.length) * 0.3

    // At narrow widths 12 month labels overlap into an unreadable blob. Draw only
    // every Nth label so adjacent labels always clear ~22px (font-size 11); bars
    // still render for every data point.
    const stepX = barWidth + barGap
    const minLabelSpacing = 22
    const labelInterval = Math.max(1, Math.ceil(minLabelSpacing / stepX))

    const svg = document.createElementNS(SVG_NS, "svg")
    svg.setAttribute("width", width)
    svg.setAttribute("height", height)

    data.forEach((d, i) => {
      const x = padding.left + i * (barWidth + barGap) + barGap / 2
      const barHeight = (d.value / maxValue) * chartHeight
      const y = padding.top + chartHeight - barHeight

      const rect = document.createElementNS(SVG_NS, "rect")
      rect.setAttribute("x", x)
      rect.setAttribute("y", y)
      rect.setAttribute("width", barWidth)
      rect.setAttribute("height", barHeight)
      rect.setAttribute("fill", "#149d71")
      rect.setAttribute("rx", "3")
      svg.appendChild(rect)

      if (i % labelInterval === 0) {
        const text = document.createElementNS(SVG_NS, "text")
        text.setAttribute("x", x + barWidth / 2)
        text.setAttribute("y", height - 5)
        text.setAttribute("text-anchor", "middle")
        text.setAttribute("class", "fill-gray-500")
        text.setAttribute("font-size", "11")
        text.textContent = d.label || ""
        svg.appendChild(text)
      }
    })

    this.canvasTarget.replaceChildren(svg)
  }
}
